/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import { property } from 'lit/decorators.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import styles from './content-section.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * The Content Section component for use with cardSection
 *
 * @element c4d-content-section
 * @slot heading - Section heading
 * @csspart layout - The component wrapper. Usage: `c4d-content-section::part(layout)`
 * @csspart leading - The leading. Usage: `c4d-content-section::part(leading)`
 * @csspart body - The body. Usage: `c4d-content-section::part(body)`
 */
@customElement(`${c4dPrefix}-content-section`)
class C4DContentSection extends StableSelectorMixin(LitElement) {
  /**
   * An optional custom class for children.
   */
  @property({ attribute: 'children-custom-class', reflect: true })
  childrenCustomClass = '';

  /**
   * Handler for @slotchange, set the first button-group-item to kind tertiary and primary for the remaining ones
   *
   * @private
   */
  private handleSlotChange(event: Event) {
    const childItems = (event.target as HTMLSlotElement)
      .assignedNodes()
      .filter(
        (node) => node.nodeType !== Node.TEXT_NODE || node!.textContent!.trim()
      );

    childItems.forEach((elem) => {
      if (
        (elem as HTMLElement).matches(
          (this.constructor as typeof C4DContentSection).linkListSelector
        )
      ) {
        (elem as HTMLElement).setAttribute('type', 'end');
        this.setAttribute('with-link-list', '');
      } else {
        this.removeAttribute('with-link-list');
      }
    });
  }

  render() {
    return html`
      <div
        class="${prefix}--content-section ${prefix}--content-section-layout"
        part="layout">
        <div class="${prefix}--content-section__leading" part="leading">
          <slot name="heading"></slot>
          <slot name="copy"></slot>
          <slot name="footer"></slot>
        </div>
        <div
          class="${prefix}--content-section__body ${this.childrenCustomClass}"
          part="body">
          <slot @slotchange="${this.handleSlotChange}"></slot>
        </div>
      </div>
    `;
  }

  static get stableSelector() {
    return `${c4dPrefix}--content-section`;
  }

  static get cardGroupSelector() {
    return `${c4dPrefix}-card-group`;
  }

  static get linkListSelector() {
    return `${c4dPrefix}-link-list`;
  }

  static get carouselSelector() {
    return `${c4dPrefix}-carousel`;
  }

  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DContentSection;
